import {
  InMemoryCacheConfig,
  FieldMergeFunction,
  Reference,
  StoreObject,
  FieldFunctionOptions,
} from '@apollo/client';
import produce from 'immer';
import {
  GetNotificationsResult,
  NotificationMessageDto,
  GetOpenNotificationsQuery,
} from '../../../generated/gql';
import GET_OPEN_NOTIFICATIONS from '../graphql/getOpenNotifications.notification';
import NOTIFICATION_MESSAGE_FRAGMENT from '../graphql/fragments/notificationMessageDTO.fragment.notification';

export type GetReferencedNotificationsResult = Pick<
  GetNotificationsResult,
  '__typename' | 'token'
> & {
  notifications: Reference[];
};

const notificationsMerge = (
  existing: GetReferencedNotificationsResult = { token: '', notifications: [] },
  incoming: GetReferencedNotificationsResult,
  options: FieldFunctionOptions,
) => {
  if (options!.variables!.skipCache) {
    return incoming;
  }

  if (
    options.variables?.token === undefined &&
    existing?.notifications?.length > 0
  ) {
    return existing;
  }

  if (existing?.notifications?.length === 0) {
    return incoming;
  }

  return produce(existing, (draft) => {
    draft.token = incoming.token;

    draft.notifications = incoming?.notifications
      ? incoming.notifications.reduce((output, input) => {
          if (output.find((n) => n.__ref === input.__ref)) {
            return output;
          }
          return [...output, input];
        }, existing.notifications || [])
      : existing.notifications;
  });
};

const putNotificationSubscriptionMerge: FieldMergeFunction = (
  existing,
  incoming: Reference | StoreObject,
  { cache, readField, toReference },
) => {
  const baseState = cache.readQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
  });

  if (!baseState?.getOpenNotifications?.notifications) return existing;

  // account for when put is an update of an existing entry instead of a new one.
  if (
    baseState.getOpenNotifications.notifications.find(
      (n) => n.uuid === readField('uuid', incoming),
    )
  ) {
    return existing;
  }

  const newNotification = cache.readFragment<NotificationMessageDto>({
    id: toReference({
      __typename: 'NotificationMessageDTO',
      uuid: readField('uuid', incoming),
    })!.__ref,
    fragment: NOTIFICATION_MESSAGE_FRAGMENT,
  })!;

  if (window.Notification && window.Notification.permission === 'granted') {
    const browserNotification = new window.Notification('PortInsider', {
      icon: 'public/favicon.ico',
      badge: 'public/favicon.ico',
      body: newNotification.subject,
      silent: false,
    });
    setTimeout(() => browserNotification.close(), 5000);
  }

  const newState = produce(baseState, (draft) => {
    draft!.getOpenNotifications!.notifications = [
      newNotification,
      ...baseState.getOpenNotifications!.notifications!,
    ];
  });

  cache!.writeQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
    data: newState!,
    variables: { skipCache: true },
  });
  cache.evict({ fieldName: 'searchNotifications' });

  return existing;
};

const removeNotificationSubscriptionMerge: FieldMergeFunction = (
  existing,
  incoming,
  { cache },
) => {
  const baseState = cache!.readQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
  });

  const newState = produce(baseState, (draft) => {
    draft!.getOpenNotifications!.notifications?.splice(
      baseState!.getOpenNotifications?.notifications?.findIndex(
        (n) => n.uuid === incoming.uuid,
      ) || 1,
      1,
    );
  });

  cache!.writeQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
    data: newState!,
    variables: { skipCache: true },
  });
  cache.evict({ fieldName: 'searchNotifications' });
  return existing;
};

const putAllNotificationsReadStatusSubscriptionMerge: FieldMergeFunction = (
  existing,
  incoming,
  { cache },
) => {
  const baseState = cache!.readQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
  });

  const newState = produce(baseState, (draft) => {
    draft!.getOpenNotifications!.notifications =
      baseState!.getOpenNotifications!.notifications!.map((notification) => {
        return { ...notification, isRead: incoming.readStatus };
      });
  });

  cache!.writeQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
    data: newState!,
    variables: { skipCache: true },
  });
  cache.evict({ fieldName: 'searchNotifications' });
  return existing;
};

const publishReadNotificationsArchivedSubscriptionMerge: FieldMergeFunction = (
  existing,
  incoming,
  { cache },
) => {
  const baseState = cache!.readQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
  });

  const newState = produce(baseState, (draft) => {
    draft!.getOpenNotifications!.notifications =
      baseState!.getOpenNotifications!.notifications!.filter(
        (notification) => !notification.isRead,
      );
  });

  cache!.writeQuery<GetOpenNotificationsQuery>({
    query: GET_OPEN_NOTIFICATIONS,
    data: newState!,
    variables: { skipCache: true },
  });
  cache.evict({ fieldName: 'searchNotifications' });
  return existing;
};

export const notificationCacheConfig: InMemoryCacheConfig = {
  typePolicies: {
    NotificationMessageDTO: {
      keyFields: ['uuid'],
    },
    Query: {
      fields: {
        getOpenNotifications: {
          keyArgs: false,
          merge: notificationsMerge as FieldMergeFunction<any, any>,
        },
        searchNotifications: {
          keyArgs: ['input', ['query']],
          merge: notificationsMerge as FieldMergeFunction<any, any>,
        },
      },
    },
    Subscription: {
      fields: {
        putNotificationSubscription: {
          keyArgs: false,
          merge: putNotificationSubscriptionMerge,
        },
        removeNotificationSubscription: {
          keyArgs: false,
          merge: removeNotificationSubscriptionMerge,
        },
        putAllNotificationsReadStatusSubscription: {
          keyArgs: false,
          merge: putAllNotificationsReadStatusSubscriptionMerge,
        },
        publishReadNotificationsArchivedSubscription: {
          keyArgs: false,
          merge: publishReadNotificationsArchivedSubscriptionMerge,
        },
      },
    },
  },
};
